import React, { createRef } from "react";
import { Accordion, Button, ButtonType, Dropdown, DropdownAlign, DropdownOrderBy, Form, IconSize, Inline, Modal, SelectListItem, Spinner, ToolbarContainer } from "@renta-apps/athenaeum-react-components";
import { BaseComponent, ch } from "@renta-apps/athenaeum-react-common";
import Warehouse from "@/models/server/Warehouse";
import { CalendarUnitTime, TaskStatusFilter, WorkOrderContractType } from "@/models/Enums";
import ConstructionSite from "@/models/server/ConstructionSite";
import BaseCalendarDataRequest from "@/pages/Calendar/BaseCalendarDataRequest";
import { Utility } from "@renta-apps/athenaeum-toolkit";
import CalendarFiltersData from "@/pages/Calendar/Toolbar/CalendarFiltersData";
import WorkOrderSearchInput from "@/components/WorkOrderSearchInput/WorkOrderSearchInput";
import CostPool from "@/models/server/CostPool";
import User from "@/models/server/User";
import Debouncer from "@/helpers/Debouncer";
import EnumProvider from "@/providers/EnumProvider";
import Localizer from "@/localization/Localizer";
import UnleashHelper from "@/helpers/UnleashHelper";
import FeatureFlags from "@/helpers/FeatureFlags";
import Region from "@/models/server/Region";

import styles from "./Toolbar.module.scss";
import UserContext from "@/models/server/UserContext";

interface ICalendarToolbarProps {
    filtersData: CalendarFiltersData;
    request: BaseCalendarDataRequest;
    startDate: Date;

    /**
     * Invoked when the user wants to create a new Work Order
     */
    onAddNewWorkOrder(): Promise<void>;

    onChange(request: BaseCalendarDataRequest, type: "prev" | "next" | "today" | null): Promise<void>;

    // openAddTaskModal(sender: IBaseComponent): Promise<void>;
    // openTeamModal(sender: IBaseComponent, team: Team | null): Promise<void>;
}

interface ICalendarToolbarState { }

export default class CalendarToolbar extends BaseComponent<ICalendarToolbarProps, ICalendarToolbarState> {

    // Fields

    public state: ICalendarToolbarState = {};

    private readonly _warehousesRef: React.RefObject<Dropdown<Warehouse>> = React.createRef();
    private readonly _constructionSitesRef: React.RefObject<Dropdown<ConstructionSite>> = React.createRef();
    private readonly _taskStatusRef: React.RefObject<Dropdown<SelectListItem>> = React.createRef();
    private readonly _workOrderContractTypeRef: React.RefObject<Dropdown<SelectListItem>> = React.createRef();
    private readonly _managersRef: React.RefObject<Dropdown<User>> = React.createRef();
    private readonly _mountersRef: React.RefObject<Dropdown<User>> = React.createRef();
    private readonly _mounterCostPoolsRef: React.RefObject<Dropdown<CostPool>> = React.createRef();
    private readonly _regionsRef: React.RefObject<Dropdown<Region>> = React.createRef();


    private readonly _legendModalRef: React.RefObject<Modal> = React.createRef();
    private readonly _searchInputRef: React.RefObject<WorkOrderSearchInput> = createRef();
    private readonly _searchAccordion: React.RefObject<Accordion> = React.createRef();
    private readonly _searchDebouncer: Debouncer = new Debouncer();

    // Properties

    private get request(): BaseCalendarDataRequest {
        return this.props.request;
    }

    private get warehouses(): Warehouse[] {
        return this.props.filtersData.warehouses;
    }

    private get costPools(): CostPool[] {
        return this.props.filtersData.costPools;
    }

    private get constructionSites(): ConstructionSite[] {
        return this.props.filtersData.constructionSites;
    }

    private get managers(): User[] {
        return this.props.filtersData.managers;
    }

    private get mounters(): User[] {
        return this.props.filtersData.mounters;
    }

    private get regions(): Region[] {
        return this.props.filtersData.regions;
    }

    private get selectedCalendarUnitType(): CalendarUnitTime {
        return this.request.calendarUnitType;
    }

    private get selectedWarehouses(): Warehouse[] {
        return this.request.warehouses;
    }

    private get selectedConstructionSites(): ConstructionSite[] {
        return this.request.constructionSites;
    }

    private get selectedMounterCostPools(): CostPool[] {
        return this.request.mounterCostPools;
    }

    private get selectedMounters(): User[] {
        return this.request.mounters;
    }

    private get selectedRegions(): Region[] {
        return this.request.regions;
    }

    private get selectedManagers(): User[] {
        return this.request.managers;
    }

    private get userContext(): UserContext {
        return ch.getContext() as UserContext;
    }

    // Methods

    private async updateFiltersRequestAsync(
        warehouses: Warehouse[] | null,
        constructionSites: ConstructionSite[] | null,
        statuses: TaskStatusFilter[] | null,
        mounterCostPools: CostPool[] | null,
        search: string | null,
        managers: User[] | null,
        mounters: User[] | null,
        calendarUnitType: CalendarUnitTime | null,
        regions: Region[] | null,
        contractTypes: WorkOrderContractType[] | null): Promise<void> {

        const request: BaseCalendarDataRequest = {
            warehouses: warehouses ?? this.props.request.warehouses,
            constructionSites: constructionSites ?? this.props.request.constructionSites,
            statuses: statuses ?? this.props.request.statuses,
            contractTypes: contractTypes ?? this.props.request.contractTypes,
            mounterCostPools: mounterCostPools ?? this.props.request.mounterCostPools,
            search: search ?? this.props.request.search,
            managers: managers ?? this.props.request.managers,
            mounters: mounters ?? this.props.request.mounters,
            calendarUnitType: calendarUnitType ?? this.props.request.calendarUnitType,
            regions: regions ?? this.props.request.regions,
        };
        const changeData: boolean = calendarUnitType != null;
        await this.props.onChange(request, changeData ? "today" : null);
    }

    private async selectWarehousesAsync(warehouses: Warehouse[]): Promise<void> {
        await this.updateFiltersRequestAsync(warehouses, null, null, null, null, null, null, null, null, null);
    }

    private async selectConstructionSitesAsync(constructionSites: ConstructionSite[]): Promise<void> {
        await this.updateFiltersRequestAsync(null, constructionSites, null, null, null, null, null, null, null, null);
    }

    private async selectMounterCostPoolsAsync(costPools: CostPool[]): Promise<void> {
        const mounters: User[] | null = UnleashHelper.isEnabled(FeatureFlags.CostPoolMounterAutoSelect)
            ? User.getWithCostPools(this.mounters, costPools)
            : null;

        await this.updateFiltersRequestAsync(null, null, null, costPools, null, null, mounters, null, null, null);
    }

    private async setTaskStatusFilterFromDropdownAsync(sender: Dropdown<SelectListItem>): Promise<void> {
        const statuses: TaskStatusFilter[] = sender.selectedItems.map(item => parseInt(item.value));
        await this.setTaskStatusFilterAsync(statuses);
    }

    private async setTaskStatusFilterAsync(statuses: TaskStatusFilter[]): Promise<void> {
        await this.updateFiltersRequestAsync(null, null, statuses, null, null, null, null, null, null, null);
    }

    private async setTaskContractTypeFilterFromDropdownAsync(sender: Dropdown<SelectListItem>): Promise<void> {
        const contractTypes: WorkOrderContractType[] = sender.selectedItems.map(item => parseInt(item.value));
        await this.setTaskContractTypeFilterAsync(contractTypes);
    }

    private async setTaskContractTypeFilterAsync(contractTypes: WorkOrderContractType[]): Promise<void> {
        await this.updateFiltersRequestAsync(null, null, null, null, null, null, null, null, null, contractTypes);
    }

    private async onManagersChangeAsync(managers: User[]): Promise<void> {
        await this.updateFiltersRequestAsync(null, null, null, null, null, managers, null, null, null, null);
    }

    private async onMountersChangeAsync(mounters: User[]): Promise<void> {
        await this.updateFiltersRequestAsync(null, null, null, null, null, null, mounters, null, null, null);
    }

    private async onRegionsChangeAsync(regions: Region[]): Promise<void> {
        await this.updateFiltersRequestAsync(null, null, null, null, null, null, null, null, regions, null);
    }

    private async setCalendarUnitTime(item: SelectListItem, userInteraction: boolean): Promise<void> {
        if (!userInteraction) {
            return;
        }
        const unit: CalendarUnitTime = parseInt(item.value);
        await this.updateFiltersRequestAsync(null, null, null, null, null, null, null, unit, null, null);
    }

    private onSearchChangeAsync = async (search: string): Promise<void> => {
        this._searchDebouncer.debounce(
            async () => {
                await this.updateFiltersRequestAsync(null, null, null, null, search, null, null, null, null, null);
            },
            500
        );
    }

    private async accordionRecalculateHeight(): Promise<void> {
        const accordionId = this._searchAccordion.current?.id;
        if (accordionId) {
            var accordianHtml = document.getElementById(accordionId.toString())?.getElementsByClassName("athenaeum-accordion-contentContainer")[0] as HTMLElement;
            if (accordianHtml) {
                accordianHtml.style.height = accordianHtml.style.maxHeight;
            }
        }

        await this._searchAccordion.current?.recalculateContentHeight();
    }

    private async clearAsync(): Promise<void> {
        await this.props.onChange(new BaseCalendarDataRequest(), "today");
    }

    private openLegendAsync = async (): Promise<void> => {
        await this._legendModalRef.current?.openAsync();
    }

    public hasSpinner(): boolean {
        return true;
    }

    public render(): React.ReactNode {
        return (
            <ToolbarContainer className={styles.toolbar}>
                {
                    (this.isSpinning()) && <Spinner />
                }

                <Form>
                    <Inline>
                        <>
                            {this.request.calendarUnitType === CalendarUnitTime.Week ? (
                                <h6>{Localizer.enumCalendarUnitTimeWeek}: {Utility.getISOWeekNumber(this.props.startDate)}</h6>
                            ) : this.request.calendarUnitType === CalendarUnitTime.TwoWeeks ? (
                                <h6>{Localizer.enumCalendarUnitTimeWeek}: {Utility.getISOWeekNumber(this.props.startDate)} - {Utility.getISOWeekNumber(this.props.startDate) + 2}</h6>
                            ) : (
                                <h6>{Localizer.enumCalendarUnitTimeMonth}: {Utility.getMonth(this.props.startDate)}</h6>
                            )}
                        </>

                        <Button id="calendarPrevButton" icon={{ name: "arrow-left", size: IconSize.Large }}
                            type={ButtonType.Orange}
                            onClick={async () => await this.props.onChange(this.props.request, "prev")}
                        />

                        <Button id="calendarNextButton" icon={{ name: "arrow-right", size: IconSize.Large }}
                            type={ButtonType.Orange}
                            onClick={async () => await this.props.onChange(this.props.request, "next")}
                        />

                        <Button id="calendarTodayButton"
                            label={Localizer.calendarPageToolbarTodayButtonLabel}
                            type={ButtonType.Orange}
                            onClick={async () => await this.props.onChange(this.props.request, "today")}
                        />

                        <Dropdown
                            id="calendarViewDropdown"
                            className={styles.viewTopMargin}
                            required small noWrap noFilter
                            orderBy={DropdownOrderBy.None}
                            items={EnumProvider.getCalendarUnitTimeItems()}
                            selectedItem={this.selectedCalendarUnitType}
                            onChange={async (_, item, userInteraction) => await this.setCalendarUnitTime(item!, userInteraction)}
                        />

                        <Button small
                            icon={{ name: "question", size: IconSize.Large }}
                            label={Localizer.genericLegend}
                            onClick={this.openLegendAsync}
                        />

                        <Button small
                            label={Localizer.genericWorkOrderNew}
                            title={Localizer.genericWorkOrderNew}
                            icon={{ name: "plus", size: IconSize.Large }}
                            type={ButtonType.Primary}
                            onClick={this.props.onAddNewWorkOrder}
                        />
                    </Inline>

                    <Accordion
                        ref={this._searchAccordion}
                        header={Localizer.tasksToolbarSearch}
                        expanded={false}
                        autoCollapse={true}
                        className={styles.filterAccordian}
                        maxHeightOffset={150}
                        onToggle={async (_sender, _expanded) => await this.accordionRecalculateHeight()}
                    >
                        <Inline>
                            <WorkOrderSearchInput ref={this._searchInputRef}
                                label={Localizer.tasksToolbarSearch}
                                width={"40ch"}
                                value={this.request.search ?? ""}
                                onChange={this.onSearchChangeAsync}
                            />

                            <Dropdown small multiple autoGroupSelected
                                ref={this._warehousesRef}
                                minWidth={200}
                                items={this.warehouses}
                                label={Localizer.genericWarehouse}
                                selectedItems={this.selectedWarehouses}
                                onToggle={async (_sender, _expanded) => await this.accordionRecalculateHeight()}
                                onChange={async (sender) => {
                                    await this.selectWarehousesAsync(sender.selectedItems);
                                    await this.accordionRecalculateHeight();
                                }}
                            />

                            <Dropdown small multiple autoGroupSelected
                                ref={this._constructionSitesRef}
                                minWidth={200}
                                items={this.constructionSites}
                                label={Localizer.genericConstructionsite}
                                selectedItems={this.selectedConstructionSites}
                                onToggle={async (_sender, _expanded) => await this.accordionRecalculateHeight()}
                                onChange={async (sender) => {
                                    await this.selectConstructionSitesAsync(sender.selectedItems);
                                    await this.accordionRecalculateHeight();
                                }}
                            />

                            <Dropdown small noValidate noSubtext noWrap noFilter multiple
                                ref={this._taskStatusRef}
                                align={DropdownAlign.Left}
                                label={Localizer.tasksPageTaskStatus}
                                minWidth={180}
                                orderBy={DropdownOrderBy.None}
                                items={EnumProvider.getTaskStatusFilterItems()}
                                selectedItems={this.request.statuses ?? []}
                                onToggle={async (_sender, _expanded) => await this.accordionRecalculateHeight()}
                                onChange={async (sender: Dropdown<SelectListItem>, _) => {
                                    await this.setTaskStatusFilterFromDropdownAsync(sender);
                                    await this.accordionRecalculateHeight();
                                }}
                            />

                            <Dropdown small noValidate noSubtext noWrap noFilter multiple
                                ref={this._workOrderContractTypeRef}
                                align={DropdownAlign.Left}
                                label={Localizer.workOrderContractTypeDropdownLabel}
                                minWidth={180}
                                orderBy={DropdownOrderBy.None}
                                items={EnumProvider.getWorkOrderContractTypeItems()}
                                selectedItems={this.request.contractTypes ?? []}
                                onToggle={async (_sender, _expanded) => await this.accordionRecalculateHeight()}
                                onChange={async (sender: Dropdown<SelectListItem>, _) => {
                                    await this.setTaskContractTypeFilterFromDropdownAsync(sender);
                                    await this.accordionRecalculateHeight();
                                }}
                            />

                            {/*<Button icon={{name: "plus", size: IconSize.Large}}*/}
                            {/*        label={Localizer.genericAddTeam}*/}
                            {/*        type={ButtonType.Orange}*/}
                            {/*        onClick={async (sender) => this.props.openTeamModal(sender, null)}*/}
                            {/*/>*/}

                        </Inline>

                        <Inline>

                            <Dropdown multiple autoGroupSelected small noValidate noSubtext noWrap
                                ref={this._managersRef}
                                label={Localizer.genericManager}
                                minWidth="180px"
                                align={DropdownAlign.Left}
                                orderBy={DropdownOrderBy.None}
                                items={this.managers}
                                selectedItems={this.selectedManagers}
                                onToggle={async (_sender, _expanded) => await this.accordionRecalculateHeight()}
                                onChange={async (sender) => {
                                    await this.onManagersChangeAsync(sender.selectedItems);
                                    await this.accordionRecalculateHeight();
                                }}
                            />

                            <Dropdown multiple autoGroupSelected small noValidate noSubtext noWrap
                                ref={this._mountersRef}
                                label={Localizer.genericMounters}
                                minWidth="180px"
                                align={DropdownAlign.Left}
                                orderBy={DropdownOrderBy.None}
                                items={this.mounters}
                                selectedItems={this.selectedMounters}
                                onToggle={async (_sender, _expanded) => await this.accordionRecalculateHeight()}
                                onChange={async (sender) => {
                                    await this.onMountersChangeAsync(sender.selectedItems);
                                    await this.accordionRecalculateHeight();
                                }}
                            />

                            <Dropdown small multiple autoGroupSelected
                                ref={this._mounterCostPoolsRef}
                                minWidth={200}
                                items={this.costPools}
                                label={Localizer.genericCostPools}
                                selectedItems={this.selectedMounterCostPools}
                                onToggle={async (_sender, _expanded) => await this.accordionRecalculateHeight()}
                                onChange={async (sender) => {
                                    await this.selectMounterCostPoolsAsync(sender.selectedItems);
                                    await this.accordionRecalculateHeight();
                                }}
                            />

                            {
                                (UnleashHelper.isEnabled(FeatureFlags.SalesPerson) && this.userContext.isSalesPerson) &&
                                <Dropdown multiple autoGroupSelected small noValidate noSubtext noWrap
                                    ref={this._regionsRef}
                                    label={Localizer.genericRegion}
                                    minWidth="180px"
                                    align={DropdownAlign.Left}
                                    orderBy={DropdownOrderBy.None}
                                    items={this.regions}
                                    selectedItems={this.selectedRegions}
                                    onToggle={async (_sender, _expanded) => await this.accordionRecalculateHeight()}
                                    onChange={async (sender) => {
                                        await this.onRegionsChangeAsync(sender.selectedItems);
                                        await this.accordionRecalculateHeight();
                                    }}
                                />
                            }

                            <Button
                                className={styles.filtersTopMargin}
                                small
                                title={Localizer.tasksToolbarClearFilters}
                                icon={{ name: "far history", size: IconSize.Large }}
                                type={ButtonType.Info}
                                onClick={async () => {
                                    await this.clearAsync();
                                    await this.accordionRecalculateHeight();
                                }}
                            />
                        </Inline>

                        { /* 
                        * Filter tiles
                        */ }
                        {
                            this.request.search && (
                                <Button small icon={{ name: "times" }}
                                    id={`filter_search`}
                                    className={styles.filterTile}
                                    type={ButtonType.Orange}
                                    label={this.request.search}
                                    title={`${Localizer.tasksToolbarSearch}`}
                                    onClick={async () => await this.onSearchChangeAsync("")} />
                            )
                        }

                        {
                            this.selectedWarehouses && this.selectedWarehouses.length > 0 && this.selectedWarehouses.map(warehouse => (
                                <Button small icon={{ name: "times" }}
                                    id={`filter_warehouse_${warehouse.id}`}
                                    className={styles.filterTile}
                                    type={ButtonType.Orange}
                                    label={warehouse.name}
                                    title={`${Localizer.genericWarehouse}`}
                                    onClick={async () => await this.selectWarehousesAsync(this.selectedWarehouses.filter(w => w.id !== warehouse.id))} />
                            ))
                        }

                        {
                            this.selectedConstructionSites && this.selectedConstructionSites.length > 0 && this.selectedConstructionSites.map(site => (
                                <Button small icon={{ name: "times" }}
                                    id={`filter_construction_site_${site.id}`}
                                    className={styles.filterTile}
                                    type={ButtonType.Orange}
                                    label={site.name}
                                    title={`${Localizer.genericConstructionsite}`}
                                    onClick={async () => await this.selectConstructionSitesAsync(this.selectedConstructionSites.filter(s => s.id !== site.id))} />
                            ))
                        }

                        {
                            this.request.statuses && this.request.statuses.length > 0 && this.request.statuses.map(status => (
                                <Button small icon={{ name: "times" }}
                                    id={`filter_status_${status}`}
                                    className={styles.filterTile}
                                    type={ButtonType.Orange}
                                    label={EnumProvider.getEnumText(nameof(TaskStatusFilter), status)}
                                    title={`${Localizer.tasksPageTaskStatus}`}
                                    onClick={async () => await this.setTaskStatusFilterAsync(this.request.statuses.filter(s => s !== status))} />
                            ))
                        }

                        {
                            this.request.contractTypes && this.request.contractTypes.length > 0 && this.request.contractTypes.map(contractType => (
                                <Button small icon={{ name: "times" }}
                                    id={`filter_contract_type_${contractType}`}
                                    className={styles.filterTile}
                                    type={ButtonType.Orange}
                                    label={EnumProvider.getEnumText(nameof(WorkOrderContractType), contractType)}
                                    title={`${Localizer.workOrderContractTypeDropdownLabel}`}
                                    onClick={async () => await this.setTaskContractTypeFilterAsync(this.request.contractTypes.filter(c => c !== contractType))} />
                            ))
                        }

                        {
                            this.selectedManagers && this.selectedManagers.length > 0 && this.selectedManagers.map(manager => (
                                <Button small icon={{ name: "times" }}
                                    id={`filter_manager_${manager.id}`}
                                    className={styles.filterTile}
                                    type={ButtonType.Orange}
                                    label={manager.firstname + ' ' + manager.lastName}
                                    title={`${Localizer.genericManager}`}
                                    onClick={async () => await this.onManagersChangeAsync(this.selectedManagers.filter(m => m.id !== manager.id))} />
                            ))
                        }

                        {
                            this.selectedMounters && this.selectedMounters.length > 0 && this.selectedMounters.map(mounter => (
                                <Button small icon={{ name: "times" }}
                                    id={`filter_mounter_${mounter.id}`}
                                    className={styles.filterTile}
                                    type={ButtonType.Orange}
                                    label={mounter.firstname + ' ' + mounter.lastName}
                                    title={`${Localizer.genericMounters}`}
                                    onClick={async () => await this.onMountersChangeAsync(this.selectedMounters.filter(m => m.id !== mounter.id))} />
                            ))
                        }

                        {
                            this.selectedMounterCostPools && this.selectedMounterCostPools.length > 0 && this.selectedMounterCostPools.map(pool => (
                                <Button small icon={{ name: "times" }}
                                    id={`filter_cost_pool_${pool.id}`}
                                    className={styles.filterTile}
                                    type={ButtonType.Orange}
                                    label={pool.name}
                                    title={`${Localizer.genericCostPools}`}
                                    onClick={async () => await this.selectMounterCostPoolsAsync(this.selectedMounterCostPools.filter(p => p.id !== pool.id))} />
                            ))
                        }

                        {
                            this.selectedRegions && this.selectedRegions.length > 0 && this.selectedRegions.map(region => (
                                <Button small icon={{ name: "times" }}
                                    id={`filter_region_${region.id}`}
                                    className={styles.filterTile}
                                    type={ButtonType.Orange}
                                    label={region.name}
                                    title={`${Localizer.genericRegion}`}
                                    onClick={async () => await this.onRegionsChangeAsync(this.selectedRegions.filter(r => r.id !== region.id))} />
                            ))
                        }
                    </Accordion>
                </Form>

                <Modal ref={this._legendModalRef}
                    className={styles.calendarLegendModal}
                    title={Localizer.genericLegend}
                >
                    <Inline>
                        <div className={styles.dotsContainer}>
                            <div className={styles.dotsColumn}>
                                <div className={styles.dot}>
                                    <div style={{ backgroundColor: "gray" }} />
                                    <span>{Localizer.enumTaskStatusFilterUnscheduled}</span>
                                </div>

                                <div className={styles.dot}>
                                    <div style={{ backgroundColor: "yellow" }} />
                                    <span>{Localizer.enumTaskStatusFilterUpcoming}</span>
                                </div>

                                <div className={styles.dot}>
                                    <div style={{ backgroundColor: "lightblue" }} />
                                    <span>{Localizer.enumTaskStatusFilterInProgress}</span>
                                </div>

                                <div className={styles.dot}>
                                    <div style={{ backgroundColor: "blue" }} />
                                    <span>{Localizer.enumTaskStatusFilterCompleted}</span>
                                </div>

                                <div className={styles.dot}>
                                    <div style={{ backgroundColor: "violet" }} />
                                    <span>{Localizer.enumTaskStatusFilterSentToCustomer}</span>
                                </div>

                                <div className={styles.dot}>
                                    <div style={{ backgroundColor: "darkviolet" }} />
                                    <span>{Localizer.enumTaskStatusFilterApprovedByCustomer}</span>
                                </div>

                                <div className={styles.dot}>
                                    <div style={{ backgroundColor: "red" }} />
                                    <span>{Localizer.enumTaskStatusFilterDeclinedByCustomer}</span>
                                </div>

                                <div className={styles.dot}>
                                    <div style={{ backgroundColor: "lightgreen" }} />
                                    <span>{Localizer.enumTaskStatusFilterReadyForInvoicing}</span>
                                </div>

                                <div className={styles.dot}>
                                    <div style={{ backgroundColor: "green" }} />
                                    <span>{Localizer.enumTaskStatusFilterInvoiced}</span>
                                </div>
                            </div>
                        </div>
                    </Inline>
                </Modal>
            </ToolbarContainer>
        );
    }
}